import { resolveGridColumnType } from '../column/meta'
import type {
  GridColumnProps,
  GridSearchField,
  GridSearchFieldMode,
} from '../types'

/** 与后端 SelectAndPageHandle.GLOBAL_SEARCH_MIN_LENGTH 对齐 */
export const GLOBAL_SEARCH_MIN_LENGTH = 2
/** 与后端 SelectAndPageHandle.GLOBAL_SEARCH_MAX_LENGTH 对齐 */
export const GLOBAL_SEARCH_MAX_LENGTH = 50
/** 与后端 GlobalSearchField.MAX_FIELDS 对齐 */
export const GLOBAL_SEARCH_MAX_FIELDS = 20
export const GLOBAL_SEARCH_DEBOUNCE_MS = 300

const SEARCH_FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]*$/
const SECRET_FIELD = /(password|passwd|secret|token)/i
const EQ_FIELDS = new Set(['mobile', 'phone', 'tel', 'idcard'])
const PREFIX_FIELDS = new Set(['username', 'email', 'code', 'no', 'sn'])
const TEXT_COLUMN_TYPES = new Set<string>(['input', 'html', 'base'])

function isSearchFieldName(field: string) {
  return SEARCH_FIELD_NAME.test(field)
}

function isSecretField(field: string) {
  return SECRET_FIELD.test(field)
}

function resolveModeByFieldName(field: string): GridSearchFieldMode {
  const lower = field.toLowerCase()
  if (EQ_FIELDS.has(lower)) {
    return 'eq'
  }
  if (PREFIX_FIELDS.has(lower) || /(?:code|no|sn)$/i.test(lower)) {
    return 'prefix'
  }
  return 'text'
}

function pushSearchField(
  out: Array<{ field: string; mode: GridSearchFieldMode }>,
  seen: Set<string>,
  field: string,
  mode: GridSearchFieldMode
) {
  if (!isSearchFieldName(field) || isSecretField(field) || seen.has(field)) {
    return
  }
  seen.add(field)
  out.push({ field, mode })
}

/**
 * 按列类型自动收集全局搜索字段。
 * 文本列进搜索；lov / switch / 日期 / 文件 / 勾选不进。
 * table 列搜 map 展示字段，不搜 id。
 */
export function collectSearchFields(
  columns?: GridColumnProps[]
): GridSearchField[] {
  if (!columns?.length) {
    return []
  }
  const out: Array<{ field: string; mode: GridSearchFieldMode }> = []
  const seen = new Set<string>()

  for (const col of columns) {
    if (col.visible === false || col.search === false) {
      continue
    }
    const type = resolveGridColumnType(col)
    const override =
      col.search === 'eq' || col.search === 'prefix' || col.search === 'text'
        ? col.search
        : undefined

    if (override) {
      if (col.field) {
        pushSearchField(out, seen, col.field.trim(), override)
      }
      continue
    }

    if (type === 'table') {
      const field = col.field?.trim()
      if (!col.map) {
        continue
      }
      for (const key of Object.keys(col.map)) {
        const displayField = key.trim()
        if (displayField && displayField !== field) {
          pushSearchField(out, seen, displayField, 'text')
        }
      }
      continue
    }

    if (!TEXT_COLUMN_TYPES.has(type) || !col.field) {
      continue
    }
    const field = col.field.trim()
    pushSearchField(out, seen, field, resolveModeByFieldName(field))
  }

  return out.slice(0, GLOBAL_SEARCH_MAX_FIELDS)
}

/** 序列化为后端 searchFields 参数：username,nickname:text,mobile:eq */
export function serializeSearchFields(
  fields?: GridSearchField[]
): string | undefined {
  if (!fields?.length) {
    return undefined
  }
  const parts: string[] = []
  for (const item of fields) {
    if (typeof item === 'string') {
      const text = item.trim()
      if (text) {
        parts.push(text)
      }
      continue
    }
    const field = item.field?.trim()
    if (!field) {
      continue
    }
    if (item.mode && item.mode !== 'prefix') {
      parts.push(`${field}:${item.mode}`)
    } else {
      parts.push(field)
    }
  }
  return parts.length ? parts.join(',') : undefined
}

export function resolveSearchFieldsParam(
  searchFields: GridSearchField[] | undefined,
  columns: GridColumnProps[] | undefined
): string | undefined {
  const fields =
    searchFields !== undefined ? searchFields : collectSearchFields(columns)
  return serializeSearchFields(fields)
}

/** 空或过短关键字不传 keyword；查询本身仍可发起 */
export function normalizeGlobalSearchKeyword(
  raw: string | undefined | null
): { keyword?: string } {
  const trimmed = (raw ?? '').trim()
  if (!trimmed || trimmed.length < GLOBAL_SEARCH_MIN_LENGTH) {
    return {}
  }
  return {
    keyword: trimmed.slice(0, GLOBAL_SEARCH_MAX_LENGTH),
  }
}
